import type { DemoScheduleBlock, SchedulingViewer } from "@emr/contracts";

/*
 * What a screen offers, from the viewer the schedule read returns. These mirror the server's
 * rules so the UI hides what it would refuse; the server still decides every write.
 */

export type ScheduleBlockKind = DemoScheduleBlock["kind"];
type Viewer = SchedulingViewer | null | undefined;

/** The in-memory prototype has no roles: everything is editable there. */
export function fullSchedulingAccess(providerIds: readonly string[]): SchedulingViewer {
  return {
    providerId: null,
    canManageSettings: true,
    canBlockClinic: true,
    calendarProviderIds: [...providerIds],
    blockProviderIds: [...providerIds],
  };
}

/** Clinic settings, the physician roster and online booking. */
export function canManageSchedulingSettings(viewer: Viewer): boolean {
  return viewer?.canManageSettings === true;
}

/** This physician's weekly hours, date overrides and calendar leave. */
export function canEditProviderCalendar(viewer: Viewer, providerId: string): boolean {
  return viewer?.calendarProviderIds.includes(providerId) === true;
}

/**
 * Creating or removing a block. Closures and holds on the whole clinic need canBlockClinic; a
 * physician's leave, unavailability or hold needs that physician in blockProviderIds.
 */
export function canManageBlock(
  viewer: Viewer,
  block: { readonly kind: ScheduleBlockKind; readonly providerId?: string | undefined },
): boolean {
  if (viewer === null || viewer === undefined) return false;
  if (block.kind === "clinic_closed" || block.providerId === undefined) {
    return viewer.canBlockClinic;
  }
  return viewer.blockProviderIds.includes(block.providerId);
}

/** The block kinds the viewer may create for one physician, or for the whole clinic. */
export function allowedBlockKinds(
  viewer: Viewer,
  providerId: string | undefined,
): readonly ScheduleBlockKind[] {
  if (providerId === undefined) {
    return canManageBlock(viewer, { kind: "clinic_closed" })
      ? ["clinic_closed", "administrative"]
      : [];
  }
  return canManageBlock(viewer, { kind: "vacation", providerId })
    ? ["provider_unavailable", "vacation", "administrative"]
    : [];
}
